import dayjs from '@/services/date/index.js'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import { Conditions } from '@/shared/constants/datePicker/picker'

/**
 * Filter type definitions for the booking filters store.
 * Determines how filters are stored, accessed, and mutated.
 */
export const FilterTypes = Object.freeze({
  /** Multi-select filters stored as arrays (statuses, educators, etc.) */
  ARRAY: 'array',
  /** Single value filters stored as strings (search query) */
  STRING: 'string',
  /** Composite filters stored as objects with multiple properties (dateFilter, timeFilter) */
  OBJECT: 'object',
})

/**
 * Configuration for all booking list filters.
 * This is the single source of truth for filter state, getters, and mutations.
 *
 * Adding a new filter:
 * 1. Add a new entry to this config object
 * 2. The store and mixin will automatically generate the necessary code
 *
 * @example
 * // Adding a new array filter:
 * subjects: {
 *   type: FilterTypes.ARRAY,
 *   stateKey: 'selectedSubjects',
 *   default: [],
 * },
 */
export const FILTER_CONFIG = Object.freeze({
  // === Selection Filters (ARRAY type) ===
  statuses: {
    type: FilterTypes.ARRAY,
    stateKey: 'selectedStatuses',
    default: [],
  },
  educators: {
    type: FilterTypes.ARRAY,
    stateKey: 'selectedEducators',
    default: [],
  },
  locations: {
    type: FilterTypes.ARRAY,
    stateKey: 'selectedLocations',
    default: [],
  },
  classifications: {
    type: FilterTypes.ARRAY,
    stateKey: 'selectedClassifications',
    default: [],
  },
  certifications: {
    type: FilterTypes.ARRAY,
    stateKey: 'selectedCertifications',
    default: [],
  },
  grades: {
    type: FilterTypes.ARRAY,
    stateKey: 'selectedGrades',
    default: [],
  },
  replacing: {
    type: FilterTypes.ARRAY,
    stateKey: 'selectedReplacing',
    default: [],
  },

  // === Composite Filters (OBJECT type) ===
  dateFilter: {
    type: FilterTypes.OBJECT,
    stateKey: 'dateFilter',
    /**
     * Getter prefix for sub-getters. Empty string means use subKey directly.
     * Results in getters: startDate, endDate, condition
     */
    getterPrefix: '',
    /**
     * Returns dynamic default values for date filter.
     * @returns {Object} Default date filter state
     */
    default: () => ({
      startDate: dayjs()
        .subtract(15, 'day')
        .format(DateFormatToken.YYYYMMDD),
      endDate: dayjs()
        .add(15, 'day')
        .format(DateFormatToken.YYYYMMDD),
      condition: Conditions.BETWEEN,
    }),
    subKeys: ['startDate', 'endDate', 'condition'],
  },
  timeFilter: {
    type: FilterTypes.OBJECT,
    stateKey: 'timeFilter',
    /**
     * Getter prefix for sub-getters. Empty string means use subKey directly.
     * Results in getters: startTime, endTime
     */
    getterPrefix: '',
    default: {
      startTime: null,
      endTime: null,
    },
    subKeys: ['startTime', 'endTime'],
  },

  // === String Filters (STRING type) ===
  searchQuery: {
    type: FilterTypes.STRING,
    stateKey: 'searchQuery',
    default: '',
  },
})

/**
 * Helper to get all array-type filter keys.
 * Useful for checking if any selection filter is active.
 * @returns {string[]} Array of filter keys
 */
export const getArrayFilterKeys = () =>
  Object.entries(FILTER_CONFIG)
    .filter(([, config]) => config.type === FilterTypes.ARRAY)
    .map(([key]) => key)

/**
 * Helper to resolve a filter's default value.
 * Handles both static defaults and function defaults.
 * Always returns fresh copies to prevent shared references.
 * @param {string} key - The filter key from FILTER_CONFIG
 * @returns {*} The default value for the filter
 */
export const getFilterDefault = (key) => {
  const config = FILTER_CONFIG[key]
  if (!config) return undefined

  if (typeof config.default === 'function') {
    return config.default()
  }

  // Return fresh copies for static defaults
  if (Array.isArray(config.default)) {
    return [...config.default]
  }
  if (config.default !== null && typeof config.default === 'object') {
    return { ...config.default }
  }

  return config.default
}
